import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Download, ArrowLeft } from 'lucide-react';

const OrderDetailsComponent = () => {
  const navigate = useNavigate();
  const [orderItems, setOrderItems] = useState([
    {
      id: 1,
      name: 'Sigiriya Fortress',
      description: 'Destination Ticket',
      merchant: 'Cultural Triangle Authority',
      ticketCategories: [
        {
          type: 'Local Adult',
          unitPrice: 100.00,
          totalTickets: 2,
          voidTickets: 0,
          voidDate: null,
          expiredTickets: 0
        },
        {
          type: 'Local Child',
          unitPrice: 50.00,
          totalTickets: 1,
          voidTickets: 0,
          voidDate: null,
          expiredTickets: 0
        },
        {
          type: 'Foreign Adult',
          unitPrice: 3000.00,
          totalTickets: 2,
          voidTickets: 1,
          voidDate: 'Apr 18, 2023',
          expiredTickets: 0
        },
        {
          type: 'Foreign Child',
          unitPrice: 1500.00,
          totalTickets: 1,
          voidTickets: 0,
          voidDate: null,
          expiredTickets: 1
        }
      ]
    }
  ]);

  const orderInfo = {
    orderId: 'ORD-23401',
    orderDate: 'April 15, 2023 14:30',
    orderStatus: 'Confirmed',
    paymentStatus: 'Success',
    paymentMethod: 'Credit Card (Visa)',
    paymentId: 'PAY-202304150123',
    currency: 'LKR (Sri Lankan Rupee)',
    totalAmount: 12500.00
  };

  const customerInfo = {
    customer: 'John Smith',
    email: 'john.smith@example.com',
    phone: '+94 77 123 4567',
    country: 'Sri Lanka',
    nicPassport: '985432789V',
    userSince: 'January 10, 2023',
    orders: 5
  };

  const calculateCategorySubtotal = (category) => {
    const activeTickets = category.totalTickets - category.voidTickets - category.expiredTickets;
    return activeTickets * category.unitPrice;
  };

  const calculateItemTotal = (item) => {
    return item.ticketCategories.reduce((sum, category) => {
      return sum + calculateCategorySubtotal(category);
    }, 0);
  };

  const calculateGrandTotal = () => {
    return orderItems.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  };

  return (
    <div className="space-y-4">
      {/* Page Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Order {orderInfo.orderId}</h1>
          <div className="mt-1 text-sm text-slate-500">
            Home <span className="mx-1.5 text-slate-400">/</span>
            Merchant Dashboard <span className="mx-1.5 text-slate-400">/</span>
            Order List <span className="mx-1.5 text-slate-400">/</span>
            <span className="font-semibold text-slate-700">{orderInfo.orderId}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button className="flex items-center px-4 py-2 text-sm font-semibold border rounded-lg border-slate-300 text-slate-600 hover:bg-slate-50">
            <Mail className="w-4 h-4 mr-2" />
            Email
          </button>
          <button className="flex items-center px-4 py-2 text-sm font-semibold border rounded-lg border-slate-300 text-slate-600 hover:bg-slate-50">
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
          <button className="px-4 py-2 text-sm font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600">
            Cancel Order
          </button>
        </div>
      </div>

      {/* Order and Customer Information */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Order Information */}
        <div className="p-6 bg-white border rounded-xl border-slate-200">
          <h2 className="mb-4 text-sm font-bold text-slate-800">Order Information</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Order ID</span>
              <span className="font-medium text-slate-800">{orderInfo.orderId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Order Date</span>
              <span className="font-medium text-brand-blue">{orderInfo.orderDate}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Order Status</span>
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-brand-blue/10 text-brand-blue">
                {orderInfo.orderStatus}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Payment Status</span>
              <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700">
                {orderInfo.paymentStatus}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Payment Method</span>
              <span className="text-slate-800">{orderInfo.paymentMethod}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Payment ID</span>
              <span className="text-slate-800">{orderInfo.paymentId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Currency</span>
              <span className="text-slate-800">{orderInfo.currency}</span>
            </div>
            <div className="flex justify-between pt-3 border-t border-slate-100">
              <span className="text-slate-500">Total Amount</span>
              <span className="font-bold text-slate-900">LKR {orderInfo.totalAmount.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Customer Information */}
        <div className="p-6 bg-white border rounded-xl border-slate-200">
          <h2 className="mb-4 text-sm font-bold text-slate-800">Customer Information</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-500">Customer</span>
              <span className="font-medium text-slate-800">{customerInfo.customer}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Email</span>
              <span className="font-medium text-brand-blue">{customerInfo.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Phone</span>
              <span className="text-slate-800">{customerInfo.phone}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Country</span>
              <span className="text-slate-800">{customerInfo.country}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">NIC / Passport</span>
              <span className="text-slate-800">{customerInfo.nicPassport}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">User Since</span>
              <span className="text-slate-800">{customerInfo.userSince}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">Orders</span>
              <span className="text-slate-800">{customerInfo.orders}</span>
            </div>
            <div className="flex justify-end pt-1">
              <a href="#" className="text-sm font-medium text-brand-blue hover:underline">View Full Profile &rarr;</a>
            </div>
          </div>
        </div>
      </div>

      {/* Order Items */}
      <div className="p-6 bg-white border rounded-xl border-slate-200">
        <h2 className="mb-4 text-sm font-bold text-slate-800">Order Items</h2>

        {orderItems.map((item) => (
          <div key={item.id} className="mb-4 overflow-hidden border rounded-lg last:mb-0 border-slate-200">
            {/* Item Header */}
            <div className="p-4 border-b bg-slate-50 border-slate-200">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900">{item.name}</h3>
                  <p className="mt-0.5 text-xs text-slate-500">{item.description}</p>
                </div>
              </div>
              <div className="mt-2 text-xs text-slate-500">
                Merchant: <span className="font-semibold text-brand-blue">{item.merchant}</span>
              </div>
            </div>

            {/* Ticket Categories Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-xs font-semibold text-left text-slate-500">Category</th>
                    <th className="px-4 py-3 text-xs font-semibold text-right text-slate-500">Unit Price</th>
                    <th className="px-4 py-3 text-xs font-semibold text-center text-slate-500">Total Tickets</th>
                    <th className="px-4 py-3 text-xs font-semibold text-center text-slate-500">Void Tickets</th>
                    <th className="px-4 py-3 text-xs font-semibold text-center text-slate-500">Void Date</th>
                    <th className="px-4 py-3 text-xs font-semibold text-center text-slate-500">Expired Tickets</th>
                    <th className="px-4 py-3 text-xs font-semibold text-center text-slate-500">Active Tickets</th>
                    <th className="px-4 py-3 text-xs font-semibold text-right text-slate-500">Subtotal</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {item.ticketCategories.map((category, index) => {
                    const activeTickets = category.totalTickets - category.voidTickets - category.expiredTickets;
                    const subtotal = calculateCategorySubtotal(category);

                    return (
                      <tr key={index} className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm">
                          <span className="font-medium text-slate-900">{category.type}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          <span className="text-slate-600">LKR {category.unitPrice.toFixed(2)}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className="font-medium text-slate-900">{category.totalTickets}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className={`font-medium ${category.voidTickets > 0 ? 'text-red-600' : 'text-slate-300'}`}>
                            {category.voidTickets}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className="text-xs text-slate-500">
                            {category.voidDate || '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className={`font-medium ${category.expiredTickets > 0 ? 'text-amber-600' : 'text-slate-300'}`}>
                            {category.expiredTickets}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className="font-medium text-emerald-600">{activeTickets}</span>
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-right text-slate-900">
                          LKR {subtotal.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-50">
                  <tr className="border-t-2 border-slate-200">
                    <td colSpan="7" className="px-4 py-3 text-sm font-semibold text-right text-slate-700">
                      Item Total
                    </td>
                    <td className="px-4 py-3 text-base font-bold text-right text-slate-900">
                      LKR {calculateItemTotal(item).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        ))}

        {/* Grand Total */}
        {/* <div className="mt-6 pt-4 border-t-2 border-gray-300">
          <div className="flex justify-end">
            <div className="text-right">
              <p className="text-gray-600 text-sm mb-1">Grand Total</p>
              <p className="text-2xl font-bold text-gray-900">LKR {calculateGrandTotal().toFixed(2)}</p>
            </div>
          </div>
        </div> */}
      </div>

      {/* Footer Actions */}
      <div className="flex justify-start">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center px-4 py-2 text-sm font-semibold bg-white border rounded-lg border-slate-300 text-slate-600 hover:bg-slate-50"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to List
        </button>
      </div>
    </div>
  );
};

export default OrderDetailsComponent;